import {
  faBuilding,
  faQuestionCircle,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import TreeSelect from 'components/common/TreeSelect';
import {
  CONDITION_TYPES,
  CUSTOM_REPORT_FIELDS,
  getFieldOperators
} from 'constants/customReportFields';
import api from 'helpers/api';
import {
  getSlaStatusOptions,
  getTicketPriorities,
  getTicketStatus
} from 'helpers/utils';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import TypeTicketsService from 'services/TypeTickets.service';
import ContactsService from '../../../../services/Contacts.service';

const http = api();

// Componente para opções dinâmicas de data
const DateValueSelector = ({ filter, index, updateFilter, operator }) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [customDates, setCustomDates] = useState({ start: null, end: null });

  // Mapeamento de labels do backend para opções do frontend
  const labelToOptionMap = {
    today: 'today',
    yesterday: 'yesterday',
    this_week: 'this_week',
    last_7_days: 'last_7_days',
    last_15_days: 'last_15_days',
    this_month: 'this_month',
    last_30_days: 'last_30_days',
    '7_days_ago': '7_days_ago',
    '15_days_ago': '15_days_ago',
    '30_days_ago': '30_days_ago'
  };

  // Detecta se o valor é um label dinâmico
  const isDynamicLabel = value => {
    return labelToOptionMap.hasOwnProperty(value);
  };

  // Inicializa o estado baseado no valor do filtro
  useEffect(() => {
    if (filter.value) {
      if (isDynamicLabel(filter.value)) {
        // Se é um label dinâmico, seleciona a opção correspondente
        setSelectedOption(labelToOptionMap[filter.value]);
      } else if (filter.value.includes('|')) {
        // Se contém |, é um período personalizado
        setSelectedOption('custom');
        const [startDate, endDate] = filter.value.split('|');
        setCustomDates({
          start: startDate ? new Date(startDate) : null,
          end: endDate ? new Date(endDate) : null
        });
      } else {
        // Se é uma data única, é data personalizada
        setSelectedOption('custom');
        setCustomDates({
          start: new Date(filter.value),
          end: null
        });
      }
    }
  }, [filter.value]);

  // Opções para operador "Está entre" (períodos)
  const periodOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'this_week', label: 'Esta semana' },
    { value: 'last_7_days', label: 'Nos últimos 7 dias' },
    { value: 'last_15_days', label: 'Nos últimos 15 dias' },
    { value: 'this_month', label: 'Este mês' },
    { value: 'last_30_days', label: 'Nos últimos 30 dias' },
    { value: 'custom', label: 'Período personalizado' }
  ];

  // Opções para outros operadores (dias atrás)
  const daysAgoOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: '7_days_ago', label: '7 dias atrás' },
    { value: '15_days_ago', label: '15 dias atrás' },
    { value: '30_days_ago', label: '30 dias atrás' },
    { value: 'custom', label: 'Data personalizada' }
  ];

  // Determina quais opções usar baseado no operador
  const dateOptions = operator === 'BETWEEN' ? periodOptions : daysAgoOptions;

  // Função para calcular datas baseadas na opção selecionada
  const calculateDateRange = option => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (option) {
      case 'today':
        return { start: today, end: today };

      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: yesterday };

      // Opções para operador "Está entre" (períodos)
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { start: startOfWeek, end: today };

      case 'last_7_days':
        const last7Days = new Date(today);
        last7Days.setDate(today.getDate() - 7);
        return { start: last7Days, end: today };

      case 'last_15_days':
        const last15Days = new Date(today);
        last15Days.setDate(today.getDate() - 15);
        return { start: last15Days, end: today };

      case 'this_month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: startOfMonth, end: today };

      case 'last_30_days':
        const last30Days = new Date(today);
        last30Days.setDate(today.getDate() - 30);
        return { start: last30Days, end: today };

      // Opções para outros operadores (dias atrás)
      case '7_days_ago':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return { start: sevenDaysAgo, end: sevenDaysAgo };

      case '15_days_ago':
        const fifteenDaysAgo = new Date(today);
        fifteenDaysAgo.setDate(today.getDate() - 15);
        return { start: fifteenDaysAgo, end: fifteenDaysAgo };

      case '30_days_ago':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return { start: thirtyDaysAgo, end: thirtyDaysAgo };

      default:
        return null;
    }
  };

  // Função para formatar data para o formato esperado pelo backend
  const formatDateForBackend = date => {
    if (!date) return '';
    return date.toISOString();
  };

  // Função para atualizar o valor do filtro
  const updateFilterValue = option => {
    setSelectedOption(option);

    if (option === 'custom') {
      // Para período/data personalizado, não atualiza o valor ainda
      return;
    }

    // Envia o label em inglês ao invés da data fixa
    if (operator === 'BETWEEN') {
      // Para operador "Está entre", envia o label do período
      updateFilter(index, 'value', option);
    } else {
      // Para outros operadores, envia o label da data
      updateFilter(index, 'value', option);
    }
  };

  // Função para atualizar datas personalizadas
  const updateCustomDates = (startDate, endDate) => {
    setCustomDates({ start: startDate, end: endDate });

    if (operator === 'BETWEEN') {
      // Para operador "Está entre", precisa de duas datas
      if (startDate && endDate) {
        const value = `${formatDateForBackend(
          startDate
        )}|${formatDateForBackend(endDate)}`;
        updateFilter(index, 'value', value);
      }
    } else {
      // Para outros operadores, apenas uma data
      if (startDate) {
        updateFilter(index, 'value', formatDateForBackend(startDate));
      }
    }
  };

  return (
    <div className="d-flex flex-column gap-2">
      <Form.Select
        size="sm"
        value={selectedOption}
        onChange={e => updateFilterValue(e.target.value)}
      >
        <option value="">Selecione um período...</option>
        {dateOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>

      {selectedOption === 'custom' && (
        <div className="d-flex gap-2">
          <DatePicker
            selected={customDates.start}
            onChange={date => updateCustomDates(date, customDates.end)}
            className="form-control form-control-sm"
            placeholderText={
              operator === 'BETWEEN' ? 'Data inicial' : 'Selecione a data'
            }
            dateFormat="dd/MM/yyyy"
            selectsStart={operator === 'BETWEEN'}
            startDate={operator === 'BETWEEN' ? customDates.start : null}
            endDate={operator === 'BETWEEN' ? customDates.end : null}
          />
          {operator === 'BETWEEN' && (
            <DatePicker
              selected={customDates.end}
              onChange={date => updateCustomDates(customDates.start, date)}
              className="form-control form-control-sm"
              placeholderText="Data final"
              dateFormat="dd/MM/yyyy"
              selectsEnd
              startDate={customDates.start}
              endDate={customDates.end}
              minDate={customDates.start}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Componente de tooltip de ajuda para condições
const ConditionHelpTooltip = () => {
  const helpText = (
    <div>
      <strong>Para que serve uma condição?</strong>
      <br />
      <strong>
        Uma condição é usada para ligar uma regra com a regra anterior.
      </strong>
      <br />
      <strong>Quando usar E ou OU?</strong>
      <br />
      <strong>E (AND):</strong> Adiciona uma nova regra que também precisa ser
      verdadeira.
      <br />
      <strong>OU (OR):</strong> Adiciona um valor alternativo. Geralmente usado
      para o mesmo campo (ex: Status é 'Concluído' OU 'Fechado').
    </div>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="condition-help">{helpText}</Tooltip>}
    >
      <span>
        <FontAwesomeIcon
          icon={faQuestionCircle}
          className="text-body-secondary ms-1"
          style={{ cursor: 'help', fontSize: '0.8rem' }}
        />
      </span>
    </OverlayTrigger>
  );
};

// Componente de pesquisa de clientes
const CustomerSearchInput = ({ value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Função para buscar clientes com debounce
  const searchCustomers = useCallback(async term => {
    if (!term || term.length < 2) {
      setCustomers([]);
      return;
    }

    setIsLoading(true);
    try {
      const filter = `name=${term}&size=1000`;
      const response = await http.customers.list(filter);

      if (response && response.content) {
        setCustomers(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Efeito para fechar o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Efeito para buscar quando o termo de pesquisa muda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCustomers(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchCustomers]);

  const handleInputChange = e => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
  };

  const handleCustomerSelect = customer => {
    setSearchTerm(customer.name);
    onChange(customer.id); // Passa o ID do cliente para o filtro
    setShowDropdown(false);
  };

  return (
    <div className="position-relative">
      <SearchInputWithSuggestions
        searchTerm={searchTerm}
        placeholder="Digite para pesquisar..."
        ref={inputRef}
        suggestions={customers}
        size="sm"
        showSuggestions={showDropdown}
        onSearchChange={e => {
          setSearchTerm(e.target.value);
          setShowDropdown(e.target.value.length > 0);
        }}
        onSuggestionSelect={handleCustomerSelect}
        renderSuggestionItem={customer => (
          <div
            key={customer.id}
            className="px-3 py-2 border-bottom cursor-pointer hover-bg-body-secondary"
            onClick={() => handleCustomerSelect(customer)}
            role="button"
            style={{
              transition: 'background-color 0.2s'
            }}
          >
            <div className="fw-bold">{customer.name}</div>
            <div className="text-muted small">
              {customer.type === 'JURIDICAL_PERSON' ? (
                <>
                  <FontAwesomeIcon icon={faBuilding} className="me-1" /> Pessoa
                  Jurídica
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUser} className="me-1" /> Pessoa
                  Física
                </>
              )}
            </div>
          </div>
        )}
      />
      <Form.Control.Feedback type="invalid">
        Cliente é obrigatório
      </Form.Control.Feedback>
    </div>
  );
};

CustomerSearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

// Componente de pesquisa de contatos
const ContactSearchInput = ({ value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [isLoading, setIsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Função para buscar contatos com debounce
  const searchContacts = useCallback(async term => {
    if (!term || term.length < 2) {
      setContacts([]);
      return;
    }

    setIsLoading(true);
    try {
      // Busca contatos com o nome que contém o termo de pesquisa
      const filter = `name=${term}&size=1000`;
      const response = await ContactsService.list(filter);

      if (response && response.content) {
        setContacts(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Efeito para fechar o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounce para a pesquisa
  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchContacts]);

  const handleInputChange = e => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    // Não chama onChange aqui para não atualizar o filtro até que um contato seja selecionado
  };

  const handleContactSelect = contact => {
    setSearchTerm(contact.name);
    onChange(contact.id); // Passa o ID do contato para o filtro
    setShowDropdown(false);
  };

  return (
    <div className="position-relative">
      <InputGroup>
        <Form.Control
          ref={inputRef}
          type="text"
          size="sm"
          placeholder={placeholder || 'Pesquisar contato...'}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          required
        />
        <Form.Control.Feedback type="invalid">
          Contato é obrigatório
        </Form.Control.Feedback>
        {isLoading && (
          <InputGroup.Text>
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </InputGroup.Text>
        )}
      </InputGroup>

      {showDropdown && contacts.length > 0 && (
        <div
          ref={dropdownRef}
          className="position-absolute bg-body-emphasis w-100 mt-1 border rounded shadow-sm z-index-dropdown"
          style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}
        >
          {contacts.map(contact => (
            <div
              key={contact.id}
              className="p-2 border-bottom cursor-pointer hover-bg-body-secondary"
              onClick={() => handleContactSelect(contact)}
              style={{ cursor: 'pointer' }}
            >
              {contact.name}
              {contact.email && (
                <div className="small text-muted">{contact.email}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterBuilder = ({ filters, onChange }) => {
  const [agents, setAgents] = useState([]);
  const [typesTicket, setTypesTicket] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [serviceCatalogs, setServiceCatalogs] = useState([]);
  const [ticketOrigins, setTicketOrigins] = useState([]);
  const [assets, setAssets] = useState([]);
  const [filtersQuery] = useState({
    page: 0,
    size: 1000,
    includeInactives: false
  });

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const data = await http.agents.listSummaryData();
        if (data) {
          const formattedAgents = data.map(agent => ({
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`.trim()
          }));
          setAgents(formattedAgents);
        }
      } catch (error) {
        console.error('Falha ao buscar agentes:', error);
      }
    };

    const fetchTypesTicket = async () => {
      const queryParams = new URLSearchParams(filtersQuery).toString();
      const response = await TypeTicketsService.list(queryParams);
      setTypesTicket(response.content);
    };

    const fetchServiceGroups = async () => {
      const queryParams = new URLSearchParams(filtersQuery).toString();
      const response = await ServiceGroupService.listSummaryData(queryParams);
      setServiceGroups(response.content);
    };

    const fetchServiceCatalogs = async () => {
      const response = await ServiceCatalogService.listInTreeView();
      setServiceCatalogs(response);
    };

    const fetchTicketOrigins = async () => {
      const response = await http.ticketOrigins.list();
      if (response && Array.isArray(response)) {
        setTicketOrigins(
          response.map(item => ({
            id: item.ticketOrigin,
            name: item.description
          }))
        );
      }
    };

    const fetchAssets = async () => {
      const queryParams = new URLSearchParams(filtersQuery).toString();
      const response = await http.assets.list(queryParams);
      if (response && response.content) {
        setAssets(response.content);
      }
    };

    fetchAgents();
    fetchTypesTicket();
    fetchServiceGroups();
    fetchServiceCatalogs();
    fetchTicketOrigins();
    fetchAssets();
  }, []);

  const addFilter = () => {
    const newFilter = {
      id: Date.now(), // Temporary ID for React key
      fieldId: '',
      operator: '',
      value: '',
      conditionType: 'AND'
    };
    onChange([...filters, newFilter]);
  };

  const updateFilter = (index, field, value) => {
    const updatedFilters = [...filters];
    updatedFilters[index] = {
      ...updatedFilters[index],
      [field]: value
    };

    // Reset operator and value when field changes
    if (field === 'fieldId') {
      updatedFilters[index].operator = '';
      updatedFilters[index].value = '';
    }

    onChange(updatedFilters);
  };

  const removeFilter = index => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange(updatedFilters);
  };

  const getFieldInfo = fieldId => {
    return CUSTOM_REPORT_FIELDS.find(f => f.fieldId === fieldId);
  };

  // Função para agrupar campos por categoria
  const groupFieldsByCategory = () => {
    const groups = {
      'Informações Básicas': [],
      'Estado do Ticket': [],
      'Datas e Prazos': [],
      Relacionamentos: [],
      'SLA e Métricas': [],
      Apontamentos: [],
      Outros: []
    };

    CUSTOM_REPORT_FIELDS.forEach(field => {
      const fieldId = field.fieldId;

      if (
        fieldId.includes('TICKET_ID') ||
        fieldId.includes('TICKET_CODE') ||
        fieldId.includes('TICKET_SUBJECT') ||
        fieldId.includes('SOURCE')
      ) {
        groups['Informações Básicas'].push(field);
      } else if (
        fieldId.includes('DATE') ||
        fieldId.includes('TIME') ||
        fieldId.includes('CREATED') ||
        fieldId.includes('COMPLETED') ||
        fieldId.includes('CLOSED') ||
        fieldId.includes('DELETED_AT')
      ) {
        groups['Datas e Prazos'].push(field);
      } else if (
        fieldId.includes('REQUESTER') ||
        fieldId.includes('CUSTOMER') ||
        fieldId.includes('ASSIGNEE') ||
        fieldId.includes('SERVICE') ||
        fieldId.includes('TYPE') ||
        fieldId.includes('ASSET')
      ) {
        groups['Relacionamentos'].push(field);
      } else if (fieldId.includes('SLA') || fieldId.includes('PERCENTAGE')) {
        groups['SLA e Métricas'].push(field);
      } else if (fieldId.includes('HOURS') || fieldId.includes('PAUSE')) {
        groups['Apontamentos'].push(field);
      } else if (
        fieldId.includes('STATUS') ||
        fieldId.includes('PRIORITY') ||
        fieldId.includes('DELETED')
      ) {
        groups['Estado do Ticket'].push(field);
      } else {
        groups['Outros'].push(field);
      }
    });

    // Remove grupos vazios
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  };

  const getOperatorsForField = fieldId => {
    const field = getFieldInfo(fieldId);
    if (!field) return [];
    return getFieldOperators(field);
  };

  const ticketStatus = getTicketStatus();
  const ticketPriorities = getTicketPriorities();
  const slaStatusOptions = getSlaStatusOptions();

  // Mapeamento de campos para suas respectivas opções de select
  const fieldToSelectOptions = {
    TICKET_ASSIGNEE: agents,
    TICKET_TYPE: typesTicket,
    TICKET_STATUS: ticketStatus,
    TICKET_SERVICE_GROUP: serviceGroups,
    TICKET_SERVICE_CATALOG: serviceCatalogs,
    TICKET_ASSET: assets,
    TICKET_PRIORITY: ticketPriorities,
    TICKET_SOURCE_CHANNEL: ticketOrigins,
    TICKET_STATUS_SLA_RESPONSE: slaStatusOptions,
    TICKET_STATUS_SLA_SOLUTION: slaStatusOptions
    // TICKET_REQUESTER agora usa o componente ContactSearchInput
  };

  const renderValueInput = (filter, index) => {
    const field = getFieldInfo(filter.fieldId);
    if (!field) return null;

    // Handle operators that don't need value input
    if (['IS_NULL', 'IS_NOT_NULL'].includes(filter.operator)) {
      return (
        <Form.Control
          type="text"
          size="sm"
          placeholder="Não aplicável"
          disabled
        />
      );
    }

    // Handle date fields with dynamic options
    if (field.dataType === 'datetime') {
      return (
        <DateValueSelector
          filter={filter}
          index={index}
          updateFilter={updateFilter}
          operator={filter.operator}
        />
      );
    }

    // Handle number input
    if (field.dataType === 'number') {
      return (
        <Form.Control
          type="number"
          size="sm"
          placeholder="Digite o valor"
          value={filter.value}
          onChange={e => updateFilter(index, 'value', e.target.value)}
          required
        />
      );
    }

    // Handle boolean input
    if (field.dataType === 'boolean') {
      return (
        <Form.Select
          size="sm"
          value={filter.value}
          onChange={e => updateFilter(index, 'value', e.target.value)}
          required
        >
          <option value="">Selecione...</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </Form.Select>
      );
    }

    // Handle duration input
    if (field.dataType === 'duration') {
      return (
        <Form.Control
          type="number"
          size="sm"
          placeholder="Digite as horas (ex: 2.5)"
          value={filter.value}
          onChange={e => updateFilter(index, 'value', e.target.value)}
          step="0.1"
          min="0"
          required
        />
      );
    }

    // Handle contact search for TICKET_REQUESTER
    if (field.fieldId === 'TICKET_REQUESTER') {
      return (
        <ContactSearchInput
          value={filter.displayValue || filter.value}
          onChange={value => updateFilter(index, 'value', value)}
          placeholder="Pesquisar contato..."
        />
      );
    }

    // Handle customer search for TICKET_CUSTOMER
    if (field.fieldId === 'TICKET_CUSTOMER') {
      return (
        <CustomerSearchInput
          value={filter.displayValue || filter.value}
          onChange={value => updateFilter(index, 'value', value)}
          placeholder="Pesquisar cliente..."
        />
      );
    }

    if (field.fieldId === 'TICKET_SERVICE_CATALOG') {
      return (
        <TreeSelect
          size="sm"
          data={serviceCatalogs}
          initialValue={filter.value ? filter.value.split(',').map(Number) : []}
          onSelect={value => updateFilter(index, 'value', value.toString())}
        />
      );
    }

    // Handle SLA status fields with select
    if (
      field.fieldId === 'TICKET_STATUS_SLA_RESPONSE' ||
      field.fieldId === 'TICKET_STATUS_SLA_SOLUTION'
    ) {
      const options = fieldToSelectOptions[field.fieldId];
      return (
        <Form.Select
          size="sm"
          value={filter.value}
          onChange={e => updateFilter(index, 'value', e.target.value)}
          required
        >
          <option value="">Selecione...</option>
          {options.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Form.Select>
      );
    }

    // Handle other relational fields with select
    if (field.hasJoin && fieldToSelectOptions[field.fieldId]) {
      const options = fieldToSelectOptions[field.fieldId];
      return (
        <Form.Select
          size="sm"
          value={filter.value}
          onChange={e => updateFilter(index, 'value', e.target.value)}
          required
        >
          <option value="">Selecione...</option>
          {options.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Form.Select>
      );
    }

    // Default text input for non-relational fields
    return (
      <Form.Control
        type="text"
        size="sm"
        placeholder="Digite o valor"
        value={filter.value}
        onChange={e => updateFilter(index, 'value', e.target.value)}
        required
      />
    );
  };

  return (
    <div className="filter-builder">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0">Configuração de Filtros</h6>
          <small className="text-danger">
            <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
            Pelo menos um filtro é obrigatório
          </small>
        </div>
        <Button size="sm" variant="outline-primary" onClick={addFilter}>
          <FontAwesomeIcon icon="plus" className="me-1" />
          Adicionar Filtro
        </Button>
      </div>

      {filters.length === 0 ? (
        <Card>
          <Card.Body className="text-center text-muted py-4">
            <FontAwesomeIcon
              icon="exclamation-triangle"
              size="2x"
              className="mb-2 text-danger"
            />
            <p className="mb-0 fw-semibold">Nenhum filtro configurado</p>
            <small className="text-danger">
              <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
              Pelo menos um filtro é obrigatório para continuar
            </small>
            <Flex className="mt-3" justifyContent="center" Ns>
              <Button size="sm" variant="primary" onClick={addFilter}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                Adicionar Primeiro Filtro
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      ) : (
        <div className="filter-list">
          {filters.map((filter, index) => {
            // Determina se deve aplicar indentação para filtros OU
            const shouldIndent = index > 0 && filter.conditionType === 'OR';
            const isFirstFilter = index === 0;

            return (
              <Card
                key={filter.id || index}
                className={`mb-3 border-0 shadow-sm ${
                  shouldIndent ? 'ms-4 border-start border-danger border-3' : ''
                }`}
                style={
                  shouldIndent
                    ? {
                        backgroundColor: 'rgba(220, 53, 69, 0.1)'
                      }
                    : {}
                }
              >
                <Card.Body className="p-4">
                  <Row className="align-items-end g-3">
                    {/* Condition Selection - Primeiro para mostrar que liga ao filtro anterior */}
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-body-secondary d-flex align-items-center">
                          Condição
                          <ConditionHelpTooltip />
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={filter.conditionType || 'AND'}
                          onChange={e =>
                            updateFilter(index, 'conditionType', e.target.value)
                          }
                          required
                          disabled={isFirstFilter}
                        >
                          {CONDITION_TYPES.map(condition => (
                            <option
                              key={condition.value}
                              value={condition.value}
                            >
                              {condition.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Condição é obrigatória
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Field Selection */}
                    <Col md={isFirstFilter ? 3 : 3}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-body-secondary">
                          Campo
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={filter.fieldId}
                          onChange={e =>
                            updateFilter(index, 'fieldId', e.target.value)
                          }
                          className="border-2"
                          required
                        >
                          <option value="">Selecione o campo...</option>
                          {Object.entries(groupFieldsByCategory()).map(
                            ([category, fields]) => (
                              <optgroup key={category} label={category}>
                                {fields.map(field => (
                                  <option
                                    key={field.fieldId}
                                    value={field.fieldId}
                                  >
                                    {field.displayName}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Campo é obrigatório
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Operator Selection */}
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="small fw-semibold text-body-secondary">
                          Operador
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={filter.operator}
                          onChange={e =>
                            updateFilter(index, 'operator', e.target.value)
                          }
                          disabled={!filter.fieldId}
                          className="border-2"
                          required
                        >
                          <option value="">Selecione...</option>
                          {getOperatorsForField(filter.fieldId).map(op => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Operador é obrigatório
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Value Input */}
                    <Col md={4}>
                      {filter.fieldId && (
                        <Form.Group>
                          <Form.Label className="small fw-semibold text-body-secondary">
                            Valor
                          </Form.Label>
                          {renderValueInput(filter, index)}
                        </Form.Group>
                      )}
                    </Col>

                    {/* Remove Button */}
                    <Col md={1} className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="border-0"
                        onClick={() => removeFilter(index)}
                        title="Remover filtro"
                      >
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    </Col>
                  </Row>

                  {/* Visual separator between filters */}
                  {index < filters.length - 1 && (
                    <div className="text-center mt-3">
                      <div className="d-inline-flex align-items-center px-3 py-1 rounded-pill">
                        <FontAwesomeIcon
                          icon="code-branch"
                          className="me-2 text-primary"
                          size="sm"
                        />
                        <small className="text-muted fw-semibold">
                          {filter.conditionType || 'AND'}
                        </small>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

ContactSearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

FilterBuilder.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      fieldId: PropTypes.string,
      operator: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired
};

export default FilterBuilder;
